import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../context/LanguageContext";
import {
  Calendar as CalendarIcon, ChevronRight, ChevronLeft,
  Sparkles, Flame, Moon, Star, Sun, Loader2, Flame as CandleIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { formatHebrewDateEn } from "../lib/hebrewGematria";
import { useAuth } from "../context/AuthContext";
import DayDetailDialog from "../components/calendar/DayDetailDialog";

// Hebrew & Gregorian month / weekday tables are pulled at render
// time from i18n. We still keep the legacy constant arrays here as
// fall-backs (and for any code path that imports them directly), so
// the Hebrew flow remains byte-identical.
// Gregorian month names in Hebrew — used for the secondary header line.
const GREG_HE = [
  "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
  "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
];

// Hebrew weekday short names. JS Date.getDay(): 0=Sun … 6=Sat.
// We render columns right-to-left so שבת sits on the left edge,
// matching the orientation of a printed Jewish wall calendar.
const WEEKDAY_HE = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];
const WEEKDAY_SHORT = ["א'", "ב'", "ג'", "ד'", "ה'", "ו'", "שבת"];

// Category → Tailwind classes for the day cell background + border.
const CATEGORY_STYLES = {
  "major-holiday": {
    cell:    "bg-gradient-to-br from-gold/35 to-gold/15 border-gold/60",
    chip:    "bg-gold/30 text-foreground",
    icon:    Star,
    iconCls: "text-gold",
  },
  "minor-holiday": {
    cell:    "bg-gold/12 border-gold/35",
    chip:    "bg-gold/20 text-foreground",
    icon:    Sparkles,
    iconCls: "text-gold",
  },
  "fast-day": {
    cell:    "bg-rose-100/40 border-rose-300/60 dark:bg-rose-950/20 dark:border-rose-800/40",
    chip:    "bg-rose-200/60 text-rose-950 dark:bg-rose-900/40 dark:text-rose-100",
    icon:    Flame,
    iconCls: "text-rose-600 dark:text-rose-400",
  },
  "rosh-chodesh": {
    cell:    "bg-card/60 border-gold/40 ring-1 ring-gold/15",
    chip:    "bg-gold/15 text-foreground",
    icon:    Moon,
    iconCls: "text-gold",
  },
  "modern": {
    cell:    "bg-sky-100/50 border-sky-300/50 dark:bg-sky-950/25 dark:border-sky-800/40",
    chip:    "bg-sky-200/60 text-sky-950 dark:bg-sky-900/40 dark:text-sky-100",
    icon:    Sun,
    iconCls: "text-sky-700 dark:text-sky-300",
  },
  "erev": {
    cell:    "bg-gold/8 border-gold/25",
    chip:    "bg-gold/15 text-foreground",
    icon:    null,
    iconCls: "",
  },
  "shabbat": {
    cell:    "bg-[#1E2A3A]/8 border-[#1E2A3A]/25 dark:bg-[#1E2A3A]/30 dark:border-[#1E2A3A]/50",
    chip:    "bg-[#1E2A3A]/15 text-foreground",
    icon:    null,
    iconCls: "",
  },
  "parsha": {
    cell:    "",   // Saturday gets shabbat styling below; parsha is shown as a chip
    chip:    "bg-[#1E2A3A]/15 text-foreground",
    icon:    null,
    iconCls: "",
  },
  "regular": {
    cell:    "bg-card/40 border-border/40",
    chip:    "",
    icon:    null,
    iconCls: "",
  },
};

// Priority order — when a day has multiple events we pick the strongest
// category to drive the background colour.
const CATEGORY_PRIORITY = [
  "major-holiday", "fast-day", "modern",
  "minor-holiday", "rosh-chodesh", "erev",
];

function pickDayCategory(dayEvents, isShabbat) {
  if (isShabbat) return "shabbat";
  for (const c of CATEGORY_PRIORITY) {
    if (dayEvents.some((e) => e.category === c)) return c;
  }
  return "regular";
}

// Personal-event color → tailwind background for the small dot/badge.
const PERSONAL_COLOR_DOT = {
  gold:   "bg-gold/80",
  navy:   "bg-[#1E2A3A]",
  green:  "bg-emerald-600",
  red:    "bg-rose-600",
  purple: "bg-violet-600",
};

function findParsha(dayEvents) {
  return dayEvents.find((e) => e.category === "parsha")?.title || null;
}

function isoToYMD(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return { y, m, d };
}

// Build the 6×7 grid array — pad pre/post days from neighbouring months
// so each row is exactly 7 cells. We don't fetch neighbour data; padding
// cells are rendered greyed-out.
function buildGrid(days, year, month) {
  if (!days.length) return [];
  const firstDow = new Date(year, month - 1, 1).getDay();   // 0=Sun … 6=Sat
  const grid = [];
  // Leading blanks for days of week before the 1st
  for (let i = 0; i < firstDow; i++) {
    grid.push({ blank: true, key: `pad-pre-${i}` });
  }
  for (const d of days) grid.push({ ...d, blank: false, key: d.date });
  // Trailing blanks to round the last row to 7
  while (grid.length % 7 !== 0) {
    grid.push({ blank: true, key: `pad-post-${grid.length}` });
  }
  return grid;
}


// --------------------------------------------------------------------- #
// Desktop & Tablet — classic 7-column grid
// --------------------------------------------------------------------- #
function CalendarGrid({ days, year, month, todayIso, onPick, selectedIso, shabbatTimes, personalByDate, isEn }) {
  const { t } = useTranslation();
  const grid = useMemo(() => buildGrid(days, year, month), [days, year, month]);
  // Localised weekday header — Hebrew defaults remain identical (the
  // i18n keys are exact mirrors of `WEEKDAY_SHORT`), English flips to
  // "Sun / Mon / Tue …".
  const weekdayHeader = useMemo(
    () => [0, 1, 2, 3, 4, 5, 6].map((i) => t(`calendar_page.weekday_short_${i}`)),
    [t]
  );

  return (
    <div
      className="rounded-lg border border-gold/40 bg-card/30 p-3 sm:p-4"
      data-testid="calendar-grid"
    >
      {/* Weekday header — Sunday on the right, Saturday on the left (RTL). */}
      <div className="grid grid-cols-7 gap-1.5 mb-2 text-center">
        {weekdayHeader.map((w, i) => (
          <div
            key={i}
            className={`text-xs font-heading tracking-[0.15em] py-1.5 ${
              i === 6 ? "text-gold" : "text-muted-foreground"
            }`}
          >
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {grid.map((cell) => {
          if (cell.blank) {
            return (
              <div
                key={cell.key}
                aria-hidden="true"
                className="aspect-square sm:aspect-[1/1.05] min-h-[78px] rounded-md bg-transparent"
              />
            );
          }
          const { y, m, d } = isoToYMD(cell.date);
          const isToday = cell.date === todayIso;
          const isSelected = cell.date === selectedIso;
          const isShabbat = new Date(y, m - 1, d).getDay() === 6;
          const cat = pickDayCategory(cell.events, isShabbat);
          const style = CATEGORY_STYLES[cat] || CATEGORY_STYLES.regular;
          const parsha = findParsha(cell.events);
          const Icon = style.icon;
          const heDay = (cell.hebrew_date || "").split(" ", 1)[0] || "";
          // In EN mode: extract the Gregorian-derived Hebrew day as an
          // arabic numeral. We compute it from the date string so we
          // don't depend on backend formatting.
          const enDay = (() => {
            if (!isEn) return "";
            const d = new Date(cell.date);
            if (Number.isNaN(d.getTime())) return "";
            try {
              const parts = new Intl.DateTimeFormat("en-u-ca-hebrew", { day: "numeric" }).formatToParts(d);
              return parts.find((p) => p.type === "day")?.value || "";
            } catch { return ""; }
          })();
          const slot = shabbatTimes?.[cell.date] || {};
          const personal = personalByDate?.[cell.date] || [];
          // Pick the headline event (skip parsha — it gets its own slot)
          const headline = cell.events
            .filter((e) => e.category !== "parsha" && e.category !== "regular")
            .sort((a, b) => CATEGORY_PRIORITY.indexOf(a.category) - CATEGORY_PRIORITY.indexOf(b.category))[0];

          return (
            <button
              key={cell.key}
              onClick={() => onPick(cell.date)}
              data-testid={`day-cell-${cell.date}`}
              data-category={cat}
              data-today={isToday ? "1" : "0"}
              className={[
                "relative aspect-square sm:aspect-[1/1.05] min-h-[78px] rounded-md border p-1.5 sm:p-2 text-right transition-all",
                "hover:shadow-[0_4px_14px_-6px_rgba(184,137,59,0.45)] hover:-translate-y-[1px]",
                style.cell,
                isToday ? "ring-2 ring-[#1E2A3A] dark:ring-gold/80 shadow-[0_0_0_3px_rgba(30,42,58,0.08)]" : "",
                isSelected && !isToday ? "ring-2 ring-gold" : "",
              ].join(" ")}
            >
              {/* Top row — Gregorian day in corner */}
              <div className="flex items-start justify-between leading-none">
                <span
                  className={`text-[10px] tracking-wider ${
                    isToday ? "text-[#1E2A3A] dark:text-gold font-semibold" : "text-muted-foreground/80"
                  }`}
                >
                  {d}
                </span>
                {Icon && (
                  <Icon className={`h-3.5 w-3.5 ${style.iconCls}`} strokeWidth={1.5} />
                )}
              </div>

              {/* Center — Hebrew gematria day */}
              <div className="mt-1 text-center font-heading text-lg sm:text-xl text-foreground">
                {isEn ? enDay : heDay}
              </div>

              {/* Headline event */}
              {headline && (
                <div
                  className={`mt-1 mx-auto text-center text-[10px] leading-tight rounded-sm px-1 py-[2px] truncate ${style.chip}`}
                  title={headline.title}
                >
                  {headline.title}
                </div>
              )}

              {/* Parsha — only on Saturday cells */}
              {parsha && (
                <div
                  className={`mt-0.5 mx-auto text-center text-[10px] leading-tight rounded-sm px-1 py-[2px] truncate bg-[#1E2A3A]/12 text-foreground`}
                  title={parsha}
                  data-testid="day-parsha"
                >
                  {parsha}
                </div>
              )}

              {/* Personal-event dots — up to 4 in the top-left (RTL).
                  Drives quick scan: "do I have anything that day?". */}
              {personal.length > 0 && (
                <div
                  className="absolute top-1 left-1 flex flex-row-reverse gap-0.5"
                  data-testid={`personal-dots-${cell.date}`}
                >
                  {personal.slice(0, 4).map((p, i) => (
                    <span
                      key={i}
                      title={p.title}
                      className={`inline-block h-1.5 w-1.5 rounded-full ${PERSONAL_COLOR_DOT[p.color] || "bg-gold/80"}`}
                    />
                  ))}
                  {personal.length > 4 && (
                    <span className="text-[8px] text-muted-foreground leading-none">+</span>
                  )}
                </div>
              )}

              {isToday && (
                <span
                  className="absolute bottom-1 left-1 text-[9px] tracking-widest text-[#1E2A3A] dark:text-gold uppercase"
                  data-testid="today-marker"
                >
                  {isEn ? "Today" : "היום"}
                </span>
              )}

              {/* Shabbat / chag candle + havdalah times. Lives in the
                  bottom-right (RTL) so it never clashes with the
                  parsha chip on the same cell. */}
              {(slot.candles || slot.havdalah) && (
                <div
                  className="absolute bottom-1 right-1 text-[9px] leading-none flex flex-col items-end gap-0.5"
                  data-testid={`day-times-${cell.date}`}
                >
                  {slot.candles && (
                    <span className="flex items-center gap-0.5 text-gold/90 font-mono" data-testid="cell-candles">
                      <CandleIcon className="h-2.5 w-2.5" strokeWidth={1.5} />
                      {slot.candles}
                    </span>
                  )}
                  {slot.havdalah && (
                    <span className="flex items-center gap-0.5 text-[#1E2A3A]/80 dark:text-gold/80 font-mono" data-testid="cell-havdalah">
                      <Moon className="h-2.5 w-2.5" strokeWidth={1.5} />
                      {slot.havdalah}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// --------------------------------------------------------------------- #
// Mobile — vertical agenda list
// --------------------------------------------------------------------- #
function AgendaList({ days, todayIso, onPick, selectedIso, shabbatTimes, personalByDate, isEn }) {
  const { t } = useTranslation();
  const weekdayLong = useMemo(
    () => [0, 1, 2, 3, 4, 5, 6].map((i) => t(`calendar_page.weekday_long_${i}`)),
    [t]
  );
  return (
    <div
      className="rounded-lg border border-gold/40 bg-card/30 divide-y divide-border/40"
      data-testid="calendar-agenda"
    >
      {days.map((day) => {
        const { y, m, d } = isoToYMD(day.date);
        const dow = new Date(y, m - 1, d).getDay();
        const isShabbat = dow === 6;
        const cat = pickDayCategory(day.events, isShabbat);
        const style = CATEGORY_STYLES[cat] || CATEGORY_STYLES.regular;
        const Icon = style.icon;
        const isToday = day.date === todayIso;
        const isSelected = day.date === selectedIso;
        const heDay = (day.hebrew_date || "").split(" ", 1)[0] || "";
        const enDay = (() => {
          if (!isEn) return "";
          const d = new Date(day.date);
          if (Number.isNaN(d.getTime())) return "";
          try {
            const parts = new Intl.DateTimeFormat("en-u-ca-hebrew", { day: "numeric" }).formatToParts(d);
            return parts.find((p) => p.type === "day")?.value || "";
          } catch { return ""; }
        })();
        const heRest = (day.hebrew_date || "").split(" ").slice(1).join(" ");
        const parsha = findParsha(day.events);
        const otherEvents = day.events.filter(
          (e) => e.category !== "parsha" && e.category !== "regular"
        );

        return (
          <button
            key={day.date}
            onClick={() => onPick(day.date)}
            data-testid={`agenda-row-${day.date}`}
            data-category={cat}
            className={[
              "w-full text-right px-3 py-3 flex items-stretch gap-3 transition-colors",
              cat !== "regular" ? style.cell.split(" ").filter(c => c.startsWith("bg-")).join(" ") : "",
              isToday ? "ring-2 ring-inset ring-[#1E2A3A] dark:ring-gold" : "",
              isSelected && !isToday ? "bg-gold/10" : "",
            ].join(" ")}
          >
            {/* Date column — Gregorian + Hebrew day */}
            <div className="flex-shrink-0 w-14 text-center border-l border-border/40 pl-3">
              <div className="text-[10px] text-muted-foreground">
                {weekdayLong[dow]}
              </div>
              <div className="font-heading text-2xl text-foreground leading-tight">
                {d}
              </div>
              <div className="font-heading text-sm text-gold leading-tight">
                {isEn ? enDay : heDay}
              </div>
            </div>

            {/* Content column */}
            <div className="flex-1 min-w-0 text-right">
              <div className="flex items-center justify-end gap-1.5 text-[11px] text-muted-foreground">
                {Icon && <Icon className={`h-3 w-3 ${style.iconCls}`} strokeWidth={1.5} />}
                <span>{heRest}</span>
              </div>
              {parsha && (
                <div className="mt-1 text-sm text-foreground/90" data-testid="agenda-parsha">
                  {parsha}
                </div>
              )}
              {otherEvents.length > 0 ? (
                <div className="mt-1 flex flex-wrap gap-1 justify-end">
                  {otherEvents.map((e, i) => (
                    <span
                      key={i}
                      className={`text-[11px] rounded-sm px-1.5 py-[2px] ${
                        CATEGORY_STYLES[e.category]?.chip || ""
                      }`}
                    >
                      {e.title}
                    </span>
                  ))}
                </div>
              ) : null}
              {/* Shabbat / chag times — one short line per event */}
              {(shabbatTimes?.[day.date]?.candles || shabbatTimes?.[day.date]?.havdalah) && (
                <div className="mt-1 flex flex-wrap gap-2 justify-end text-[11px]">
                  {shabbatTimes[day.date].candles && (
                    <span className="font-mono text-gold/90 flex items-center gap-1" data-testid="agenda-candles">
                      <Sun className="h-3 w-3" strokeWidth={1.5} />
                      {t("day_detail.candle_lighting_shabbat")} {shabbatTimes[day.date].candles}
                    </span>
                  )}
                  {shabbatTimes[day.date].havdalah && (
                    <span className="font-mono text-[#1E2A3A]/80 dark:text-gold/80 flex items-center gap-1" data-testid="agenda-havdalah">
                      <Moon className="h-3 w-3" strokeWidth={1.5} />
                      {t("day_detail.exit_shabbat")} {shabbatTimes[day.date].havdalah}
                    </span>
                  )}
                </div>
              )}
              {/* Personal events */}
              {(personalByDate?.[day.date]?.length || 0) > 0 && (
                <div className="mt-1 flex flex-wrap gap-1 justify-end" data-testid={`agenda-personal-${day.date}`}>
                  {personalByDate[day.date].map((p, i) => (
                    <span
                      key={i}
                      className={`inline-flex items-center gap-1 text-[11px] rounded-full pl-2 pr-1 py-[2px] bg-card/70 border border-border/50`}
                    >
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${PERSONAL_COLOR_DOT[p.color] || "bg-gold/80"}`} />
                      {p.title}
                    </span>
                  ))}
                </div>
              )}
              {!parsha && otherEvents.length === 0 && !isToday && (
                <div className="mt-1 text-xs text-muted-foreground/70">—</div>
              )}
              {isToday && (
                <div
                  className="mt-1 inline-block text-[10px] tracking-widest text-[#1E2A3A] dark:text-gold uppercase"
                  data-testid="agenda-today-marker"
                >
                  {t("calendar_page.today_label")}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}


// --------------------------------------------------------------------- #
// Page
// --------------------------------------------------------------------- #
export default function HebrewCalendar() {
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const today = useMemo(() => {
    const d = new Date();
    return {
      iso: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
      year: d.getFullYear(),
      month: d.getMonth() + 1,
    };
  }, []);

  const [year, setYear]   = useState(today.year);
  const [month, setMonth] = useState(today.month);
  const [data, setData]   = useState(null);
  const [todayInfo, setTodayInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIso, setSelectedIso] = useState(null);
  const [shabbatTimes, setShabbatTimes] = useState({});
  const [dialogDate, setDialogDate] = useState(null);
  const [personalByDate, setPersonalByDate] = useState({});  // YYYY-MM-DD → occurrence[]

  // Resolve the user's preferred location across the 4 sources, in
  // priority order:
  //   1. Server preference (user.preferred_location) — may be a
  //      descriptor object or a legacy catalog string.
  //   2. New localStorage key (full descriptor JSON, set by the new
  //      LocationPicker for catalog AND custom picks).
  //   3. Legacy localStorage `tanakh_zmanim_loc` (catalog string only)
  //      — back-compat for users who chose a city before this fix.
  //   4. `"jerusalem"` default — NOT bet shemesh.
  const [location, setLocation] = useState(() => {
    try {
      const stored = localStorage.getItem("tanakh_pref_location");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed) return parsed;
      }
    } catch {}
    try {
      const legacy = localStorage.getItem("tanakh_zmanim_loc");
      if (legacy) return legacy;
    } catch {}
    return "jerusalem";
  });
  // When the user object lands (or refreshes), sync from server.
  useEffect(() => {
    const pref = user?.preferred_location;
    if (!pref) return;
    // Avoid resetting if the same value is already active. Compare by
    // JSON identity for descriptors, raw equality for strings.
    const sameRef = JSON.stringify(pref) === JSON.stringify(location);
    if (sameRef) return;
    setLocation(pref);
    try { localStorage.setItem("tanakh_pref_location", JSON.stringify(pref)); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.preferred_location]);

  // Detect mobile once and react to resize — drives agenda vs grid.
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Fetch personal-event occurrences for the visible month.
  const fetchPersonal = useMemo(() => () => {
    if (!user) { setPersonalByDate({}); return; }
    const lastDay = new Date(year, month, 0).getDate();
    const fromIso = `${year}-${String(month).padStart(2,"0")}-01`;
    const toIso   = `${year}-${String(month).padStart(2,"0")}-${String(lastDay).padStart(2,"0")}`;
    api.myEventsList({ from: fromIso, to: toIso })
      .then((d) => {
        const map = {};
        for (const o of (d.occurrences || [])) {
          (map[o.occurrence_date] = map[o.occurrence_date] || []).push(o);
        }
        setPersonalByDate(map);
      })
      .catch(() => setPersonalByDate({}));
  }, [user, year, month]);

  useEffect(() => { fetchPersonal(); }, [fetchPersonal]);

  // Fetch month + today on mount and every navigation.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.calendarMonth(year, month),
      todayInfo ? Promise.resolve(todayInfo) : api.calendarToday(),
      api.calendarMonthShabbatTimes(year, month, location).catch(() => ({ times: {} })),
    ])
      .then(([m, t, st]) => {
        if (cancelled) return;
        setData(m);
        setTodayInfo(t);
        setShabbatTimes(st?.times || {});
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, location]);

  // Day picker click — also opens the detailed modal.
  const handlePick = (iso) => {
    setSelectedIso(iso);
    setDialogDate(iso);
  };

  const dialogDay = useMemo(() => {
    if (!dialogDate || !data?.days) return null;
    return data.days.find((d) => d.date === dialogDate) || null;
  }, [dialogDate, data]);
  const dialogParts = useMemo(() => {
    if (!dialogDate) return null;
    const [y, m, d] = dialogDate.split("-").map(Number);
    return { y, m, d };
  }, [dialogDate]);

  const goPrev = () => {
    setSelectedIso(null);
    if (month === 1) { setYear(year - 1); setMonth(12); }
    else setMonth(month - 1);
  };
  const goNext = () => {
    setSelectedIso(null);
    if (month === 12) { setYear(year + 1); setMonth(1); }
    else setMonth(month + 1);
  };
  const goToday = () => {
    setSelectedIso(null);
    setYear(today.year);
    setMonth(today.month);
  };

  // Header strings. We pull the Hebrew month name from the 15th of the
  // current month — guaranteed to be inside a single Hebrew month no
  // matter how the Gregorian month aligns to the Hebrew one.
  // In Hebrew mode we strip the day token from the backend's gematria
  // date ("ט״ו בסיון תשפ״ו" → "בסיון תשפ״ו"). In English mode we
  // compute the transliteration FE-side from the same Gregorian
  // anchor day and strip its leading day number ("15 Sivan 5786" →
  // "Sivan 5786") for the month header.
  const headerHebrew = useMemo(() => {
    if (!data?.days?.length) return "";
    const mid = data.days[Math.min(14, data.days.length - 1)];
    if (isEn) {
      const gregStr = mid?.gregorian_date || mid?.date;
      if (gregStr) {
        const d = new Date(gregStr);
        if (!Number.isNaN(d.getTime())) {
          const full = formatHebrewDateEn(d);
          // "DD Month YYYY" → drop the leading day number.
          return full.replace(/^\d+\s+/, "");
        }
      }
      return "";
    }
    const tokens = (mid?.hebrew_date || "").split(" ");
    return tokens.length >= 3 ? tokens.slice(1).join(" ") : (mid?.hebrew_date || "");
  }, [data, isEn]);

  return (
    <main
      className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10"
      data-testid="hebrew-calendar-page"
    >
      {/* Title row */}
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2.5">
          <CalendarIcon className="h-6 w-6 text-gold" strokeWidth={1.4} />
          <h1
            className="font-heading text-2xl sm:text-3xl text-foreground tracking-wide"
            data-testid="calendar-title"
          >
            {t("calendar_page.title")}
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {user && (
            <button
              onClick={() => navigate("/calendar/my-events")}
              className="text-xs text-gold hover:text-foreground border border-gold/40 rounded-sm px-3 py-1.5 transition-colors"
              data-testid="my-events-link"
            >
              {t("calendar_page.my_events")}
            </button>
          )}
          {todayInfo && (
            <div
              className="text-xs sm:text-sm text-muted-foreground text-right"
              data-testid="calendar-today-summary"
            >
              <div>
                {t("calendar_page.today_label")}:{" "}
                <span className="text-foreground font-medium">
                  {isEn
                    ? formatHebrewDateEn(new Date())
                    : todayInfo.hebrew_date}
                </span>
              </div>
              {todayInfo.parsha_of_week && (
                <div className="text-gold/90">
                  {t("calendar_page.this_week")}: {todayInfo.parsha_of_week}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Month header + navigation */}
      <div
        className="flex items-center justify-between gap-2 mt-4 mb-4 pb-4 border-b border-gold/25 flex-wrap"
        data-testid="calendar-nav"
      >
        <div className="flex items-center gap-2">
          {/* RTL note: prev is to the right (chevron-right), next to the left. */}
          <Button
            onClick={goPrev}
            variant="outline"
            size="sm"
            data-testid="calendar-prev-btn"
            className="border-gold/40 hover:bg-gold/10"
          >
            <ChevronRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
            {t("calendar_page.previous")}
          </Button>
          <Button
            onClick={goToday}
            variant="outline"
            size="sm"
            data-testid="calendar-today-btn"
            className="border-gold/40 hover:bg-gold/10"
          >
            {t("calendar_page.today_btn")}
          </Button>
          <Button
            onClick={goNext}
            variant="outline"
            size="sm"
            data-testid="calendar-next-btn"
            className="border-gold/40 hover:bg-gold/10"
          >
            {t("calendar_page.next")}
            <ChevronLeft className="h-4 w-4 mr-1" strokeWidth={1.5} />
          </Button>
        </div>
        <div className="text-right" data-testid="calendar-month-label">
          <div className="font-heading text-xl sm:text-2xl text-foreground">
            {headerHebrew || "—"}
          </div>
          <div className="text-xs sm:text-sm text-muted-foreground">
            {t(`calendar_page.greg_month_${month}`)} {year}
          </div>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="grid place-items-center py-24" data-testid="calendar-loading">
          <Loader2 className="h-5 w-5 animate-spin text-gold" />
        </div>
      ) : !data?.days?.length ? (
        <div className="text-center text-muted-foreground py-24">
          {t("calendar_page.load_failed")}
        </div>
      ) : isMobile ? (
        <AgendaList
          days={data.days}
          todayIso={today.iso}
          onPick={handlePick}
          selectedIso={selectedIso}
          shabbatTimes={shabbatTimes}
          personalByDate={personalByDate}
          isEn={isEn}
        />
      ) : (
        <CalendarGrid
          days={data.days}
          year={year}
          month={month}
          todayIso={today.iso}
          onPick={handlePick}
          selectedIso={selectedIso}
          shabbatTimes={shabbatTimes}
          personalByDate={personalByDate}
          isEn={isEn}
        />
      )}

      {/* Legend */}
      <div className="mt-6 flex flex-wrap items-center justify-end gap-x-4 gap-y-2 text-xs text-muted-foreground" data-testid="calendar-legend">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-gold/60 bg-gradient-to-br from-gold/35 to-gold/15"></span>
          {t("calendar_page.legend_major_holiday")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-gold/35 bg-gold/12"></span>
          {t("calendar_page.legend_minor_holiday")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-rose-300/60 bg-rose-100/50"></span>
          {t("calendar_page.legend_fast")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-gold/40 ring-1 ring-gold/15 bg-card/60"></span>
          {t("calendar_page.legend_rosh_chodesh")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-[#1E2A3A]/30 bg-[#1E2A3A]/10"></span>
          {t("calendar_page.legend_shabbat")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-gold/25 bg-gold/8"></span>
          {t("calendar_page.legend_erev")}
        </span>
      </div>

      {/* Day detail modal — reused by all click targets (grid + agenda). */}
      <DayDetailDialog
        open={!!dialogDate}
        onClose={() => setDialogDate(null)}
        day={dialogDay}
        gregYear={dialogParts?.y}
        gregMonth={dialogParts?.m}
        gregDay={dialogParts?.d}
        isAuthed={!!user}
        initialLocation={location}
        personalEvents={dialogDate ? (personalByDate[dialogDate] || []) : []}
        onPersonalChanged={fetchPersonal}
      />
    </main>
  );
}
